package com.xorrus.trading.lot.relayclient.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * Opening Stock — Remote-Typisierung analog [OpeningPutParams] (Spec §3-Muster).
 *
 * Long-only Intraday-Setup auf US-Aktien, Checklisten-Muster wie der Opening Put: im
 * Handelsfenster 10:00–10:20 NY (oder zeitgesteuert um 10:10 NY) wird der Market-Tide
 * (Unusual Whales) geprüft; bei Tide > 0 werden die Top-N Aktien mit dem höchsten positiven
 * Net-Flow als Long gekauft — jede Position mit gleichem Zielmarktwert. Der Tide ist ein reiner
 * Long-only-Filter, kein Richtungsentscheider (Tide <= 0 → kein Trade).
 *
 * Der Sofort-Start ist zweistufig (Compliance): `strategy.preview` liefert die Picks
 * ([OpeningStockPreview]), der Nutzer bestätigt sie als [OpeningStockParams.expectedPicks].
 * Zeitgesteuerter Start und „Vorbedingungen ignorieren" sind teamintern ([ChecklistGates]).
 */

val OPENING_STOCK_STRATEGY_ID = StrategyId("openingStock")
const val OPENING_STOCK_SCHEMA_VERSION = 1

@Serializable
@SerialName("openingStock")
data class OpeningStockParams(
    /** Konto (dynamische Auswahl, [OpeningStockOptions]) */
    val accountId: AccountId,
    /** An Börse übertragen */
    val transmitToExchange: Boolean,
    /** Zielmarktwert je Position in USD (ganze Dollar, Desktop-Default 2500) */
    val targetMarketValue: Int,
    /** Anzahl der Top-Picks (Long-Positionen), Desktop-Default 3 */
    val topN: Int,
    /**
     * PreCheck (Wochentag/Zeitfenster, Market-Tide) übergehen — desktopseitig zusätzlich an
     * die Lizenz FEATURE_IGNORE_PRECONDITION gebunden. Teaminterne Funktion, nicht für Kunden.
     */
    val ignorePreconditions: Boolean = false,
    /**
     * erst bis 10:10 NY warten (Pendant zum „zeitgesteuert planen"-Button der View) —
     * desktopseitig an FEATURE_TIME_BASED_TRADING gebunden. Teaminterne Funktion, nicht für
     * Kunden; platziert ohne erneute Bestätigung der Picks.
     */
    val waitForWindow: Boolean = false,
    /**
     * Vom Nutzer bestätigte Picks aus `strategy.preview` ([OpeningStockPreview.picks]). PFLICHT
     * für den Sofort-Start (waitForWindow = false): Der Desktop ermittelt die Picks beim
     * Platzieren erneut und platziert nur bei denselben Aktien — mit den bestätigten
     * Stückzahlen. Bei waitForWindow = true ignoriert. Nicht in Defaults/Presets persistieren.
     */
    val expectedPicks: List<RemoteStockPick>? = null,
    override val schemaVersion: Int = OPENING_STOCK_SCHEMA_VERSION,
) : StrategyParams

/** Ein Pick der Vorschau: Aktie + Stückzahl. */
@Serializable
data class RemoteStockPick(val ticker: String, val shares: Int) {
    /** Text-Form für die Formular-Bestätigung (`StrategyFormCodec.parse(selection = …)`): „AAPL:12". */
    val token: String get() = "$ticker:$shares"

    companion object {
        /** Gegenstück zu [token]; null bei ungültiger Form. */
        fun fromToken(token: String): RemoteStockPick? {
            val ticker = token.substringBefore(':', "").trim()
            val shares = token.substringAfter(':', "").trim().toIntOrNull()
            return if (ticker.isNotEmpty() && shares != null && shares > 0) RemoteStockPick(ticker, shares) else null
        }
    }
}

@Serializable
@SerialName("openingStock")
data class OpeningStockOptions(
    val accounts: List<AccountRef>,
    override val asOf: Instant,
    /** Lizenz-Freigaben der teaminternen Schalter ([ChecklistGates]); Default gesperrt. */
    override val ignorePreconditionsAllowed: Boolean = false,
    override val waitForWindowAllowed: Boolean = false,
) : StrategyOptions, ChecklistGates

/**
 * Ergebnis von `strategy.preview` (Compliance-Vorschau für den Sofort-Start): Market-Tide und die
 * aus Live-Daten ermittelten Picks samt anzeigefertiger Orderzeilen. Der Remote-Nutzer bestätigt
 * genau diese Picks als [OpeningStockParams.expectedPicks]. KEIN Cache — jede Vorschau rechnet frisch.
 */
@Serializable
data class OpeningStockPreview(
    /** Market-Tide (Net-Call − Net-Put-Premium, USD); null = nicht ermittelt. */
    val marketTide: Double? = null,
    /** Platzierbare Picks; leer = nichts ermittelt (siehe [message]). */
    val picks: List<RemoteStockPick> = emptyList(),
    /** Anzeigefertige Zeile pro Order (Entry je Aktie, EOD-Exit). */
    val orders: List<String> = emptyList(),
    /** Fehlermeldung, wenn keine Picks ermittelt werden konnten. */
    val message: String? = null,
    /** Zweisprachige Fassung von [message]. */
    val messageText: RemoteText? = null,
    val asOf: Instant,
)

// ---- Status-Projektion: Market-Tide + geplante/platzierte Long-Trades -----------------

/** Zustand eines einzelnen geplanten/platzierten Long-Trades (Spiegel des Business-Enums). */
@Serializable
enum class RemoteStockTradeStatus { PLANNED, SKIPPED, PLACED, FILLED, REJECTED, CLOSED }

/** Ein einzelner Long-Trade in der Remote-Projektion (Ticker, Stückzahl, Zustand). */
@Serializable
data class RemoteStockTrade(
    val ticker: String,
    val shares: Int,
    val status: RemoteStockTradeStatus = RemoteStockTradeStatus.PLANNED,
    val info: String? = null,
    /** Zweisprachige Fassung von [info]; null bei alten Desktops. */
    val infoText: RemoteText? = null,
)

@Serializable
@SerialName("openingStock")
data class OpeningStockStatus(
    override val phase: StrategyPhase,
    override val activeParams: StrategyParams? = null,
    override val since: Instant? = null,
    override val message: String? = null,
    override val messageText: RemoteText? = null,
    /** Market-Tide (Net-Call − Net-Put-Premium, USD) zur Handelszeit; null solange nicht bestimmt.
     *  Ersetzt das frühere Feld `spxMove` — alte Gegenstellen lesen dank `ignoreUnknownKeys` einfach null. */
    val marketTide: Double? = null,
    /** geplante/platzierte Long-Trades des laufenden bzw. letzten Laufs. */
    val trades: List<RemoteStockTrade> = emptyList(),
    /** PreCheck-Schritte (Zeitfenster, Market-Tide, Picks); leer bei alten Desktops. */
    val steps: List<RemoteChecklistStep> = emptyList(),
    /** PreCheck wurde für diesen Lauf übersprungen */
    val preconditionsIgnored: Boolean = false,
) : RemoteStrategyStatus {
    override val strategyId: StrategyId get() = OPENING_STOCK_STRATEGY_ID
}

// ---- Bounds -------------------------------------------------------------------------

data class OpeningStockBounds(
    /** max. Zielmarktwert je Position (USD) — Betreiber-Obergrenze gegen Fat-Finger. */
    val maxTargetMarketValue: Int? = 10_000,
    /** max. Anzahl gleichzeitiger Long-Positionen. */
    val maxTopN: Int? = 5,
    val allowTransmitToExchange: Boolean = false,
) : ParamsBounds<OpeningStockParams> {

    override fun violations(params: OpeningStockParams): List<String> = violationsText(params).map { it.de }

    override fun violationsText(params: OpeningStockParams): List<RemoteText> {
        val violations = mutableListOf<RemoteText>()
        if (maxTargetMarketValue != null && params.targetMarketValue > maxTargetMarketValue) {
            violations += RemoteText(
                de = "targetMarketValue ${params.targetMarketValue} überschreitet das Limit $maxTargetMarketValue",
                en = "targetMarketValue ${params.targetMarketValue} exceeds the limit $maxTargetMarketValue",
            )
        }
        if (maxTopN != null && params.topN > maxTopN) {
            violations += RemoteText(
                de = "topN ${params.topN} überschreitet das Limit $maxTopN",
                en = "topN ${params.topN} exceeds the limit $maxTopN",
            )
        }
        if (params.transmitToExchange && !allowTransmitToExchange) {
            violations += RemoteText(
                de = "transmitToExchange ist für Remote-Aktivierungen nicht freigegeben",
                en = "transmitToExchange is not enabled for remote activations",
            )
        }
        return violations
    }
}

// ---- Geteilte Validierung (§3.2) ------------------------------------------------------

fun validateOpeningStock(params: OpeningStockParams, options: OpeningStockOptions?): List<ValidationError> {
    val errors = mutableListOf<ValidationError>()

    if (params.schemaVersion != OPENING_STOCK_SCHEMA_VERSION) {
        errors += ValidationError(
            field = "schemaVersion",
            message = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $OPENING_STOCK_SCHEMA_VERSION) — Controller-App aktualisieren",
            code = ValidationCode.UNSUPPORTED_SCHEMA,
            messageText = RemoteText(
                de = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $OPENING_STOCK_SCHEMA_VERSION) — Controller-App aktualisieren",
                en = "Unknown schemaVersion ${params.schemaVersion} (supported: $OPENING_STOCK_SCHEMA_VERSION) — update the controller app",
            ),
        )
    }
    if (params.targetMarketValue !in 1..1_000_000) {
        errors += ValidationError(
            "targetMarketValue",
            "Zielmarktwert muss zwischen 1 und 1.000.000 liegen",
            messageText = RemoteText(
                de = "Zielmarktwert muss zwischen 1 und 1.000.000 liegen",
                en = "Target market value must be between 1 and 1,000,000",
            ),
        )
    }
    if (params.topN !in 1..20) {
        errors += ValidationError(
            "topN",
            "Anzahl (Top-N) muss zwischen 1 und 20 liegen",
            messageText = RemoteText(
                de = "Anzahl (Top-N) muss zwischen 1 und 20 liegen",
                en = "Count (top N) must be between 1 and 20",
            ),
        )
    }
    if (options != null && options.accounts.none { it.id == params.accountId }) {
        errors += ValidationError(
            field = "accountId",
            message = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
            code = ValidationCode.STALE_OPTION,
            messageText = RemoteText(
                de = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
                en = "The selected account is not (or no longer) available — reload the selection",
            ),
        )
    }
    errors += checklistGateErrors(params.ignorePreconditions, params.waitForWindow, options)
    return errors
}
